import * as React from "react";
import { Box, Card, Chip, Link, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon, TrashIcon } from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useLocation, useParams } from "react-router-dom";

import { AccountUser, accountUsersKeys, useDeleteAccountUser } from "@/hooks/api/use-account-users";
import { useAccount } from "@/hooks/api/use-accounts";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { useAuth } from "@/components/core/auth-provider";
import { RouterLink } from "@/components/core/link";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { AddUser } from "./addUser";

export function UserManagement(): React.JSX.Element {
	const { account: currentAccount, user: currentUser } = useAuth();
	const location = useLocation();

	const { accountId = currentAccount?.id } = useParams();

	const [isAddUserDrawerOpen, setIsAddUserDrawerOpen] = React.useState(false);
	const [selectedUserToAdd, setSelectedUserToAdd] = React.useState<AccountUser | null>(null);

	const isFromAccountPage = location.pathname.startsWith("/accounts");

	const {
		items: users,
		isLoading,
		totalItems,
		refetchData: fetchUsers,
	} = useCachedTableStateWithUrl<AccountUser>({
		queryKey: accountUsersKeys.list(accountId as string) as any,
		endpoint: `/accounts/${accountId}/users`,
	});

	const { data: account, isLoading: isAccountLoading } = useAccount(accountId || "");

	const {
		data: selectedUserToDelete,
		open: isDeleteUserModalOpen,
		handleClose: handleDeleteUserModalClose,
		handleOpen: handleDeleteUserModalOpen,
	} = useDialog<AccountUser | null>();

	const deleteUserMutation = useDeleteAccountUser();

	const handleDeleteUser = async () => {
		if (selectedUserToDelete && accountId) {
			try {
				await deleteUserMutation.mutateAsync({ accountId, userId: selectedUserToDelete.user.id });
				handleDeleteUserModalClose();
			} catch {
				// Error handling is done in the mutation
			}
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
				getCellValue: (row: any) => `${row.user.name}`,
			},
			{
				header: {
					id: "email",
					label: "Email",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "email",
				getCellValue: (row: any) => `${row.user.email}`,
			},
			{
				header: {
					id: "role",
					label: "Role",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "role",
				getCellValue: (row: any) => {
					return <Chip color="primary" variant="soft" size="small" label={row.role?.title} />;
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								...(row?.user?.email === currentUser?.email ? [] : [{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
										onClick: () => {
											setIsAddUserDrawerOpen(true);
											setSelectedUserToAdd(row);
										},
									}]),
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteUserModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, [currentUser]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						{isFromAccountPage && (
							<Breadcrumbs>
								<Link
									color="text.primary"
									component={RouterLink}
									href={"/accounts"}
									sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
									variant="subtitle2"
								>
									<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
									Accounts
								</Link>

								<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
									/
								</Typography>
								<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
									{isAccountLoading ? (
										<CustomSkeleton variant="rounded" width={100} height={16} />
									) : (
										`${account?.name || "Account"}`
									)}
								</Typography>
							</Breadcrumbs>
						)}
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">User Management</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage account users and their details
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									startIcon={<PlusIcon />}
									variant="contained"
									onClick={() => {
										setIsAddUserDrawerOpen(true);
										setSelectedUserToAdd(null);
									}}
								>
									Add User
								</Button>
							</Box>
						</Stack>
					</Stack>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={users}
								refetchData={fetchUsers}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteUserModalOpen}
				onClose={handleDeleteUserModalClose}
				onDelete={handleDeleteUser}
				title="Delete User"
				description="Are you sure you want to delete this user?"
			/>

			<AddUser
				account={account}
				user={selectedUserToAdd}
				isAddUserDrawerOpen={isAddUserDrawerOpen}
				onAddUserDrawerClose={() => {
					setIsAddUserDrawerOpen(false);

					setTimeout(() => {
						setSelectedUserToAdd(null);
					}, 300);
				}}
			/>
		</React.Fragment>
	);
}
